import React, {useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {doc, getDoc, updateDoc} from 'firebase/firestore';
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    OutlinedInput,
    Button,
    Card,
    CardMedia,
    CardContent, ImageListItem, ImageList
} from '@mui/material';
import {useLoading} from "../../../components/context/LoadingContext";
import {db, deleteFileByUrl, eventsCollectionName, storage} from "../../../components/Firebase/Firebase";
import {CiImageOn, CiSquarePlus} from "react-icons/ci";
import {getDownloadURL, ref, uploadBytes} from "firebase/storage";

const DistanceEditPage = () => {

    const initialDistanceState = {
        id: '',
        title: '',
        description: '',
        distance: '',
        elevationGain: 0,
        equipmentInfo: {
            content: '',
            title: 'Обязательная экипировка'
        },
        isEnabled: false,
        itraDetailsUrl: '',
        itraPoints: 0,
        registrationUrl: '',
        startDate: '',
        startTime: '',
        timeLimit: '',
        trackImageUrl: '',
        trackUrl: '',
        resultPhotoServiceApiId: '',
        resultPhotoServiceApiExternalId: '',
        foodPoints: []
    };

    const {eventId, distanceId} = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useLoading();
    const [distance, setDistance] = useState(initialDistanceState);

    const [previewImage, setPreviewImage] = useState(null);
    const [trackFile, setTrackFile] = useState(null);
    const [oldUrls, setOldUrls] = useState([]);

    const [errors, setErrors] = useState({
        id: false,
        title: false,
        distance: false,
        startDate: false,
        foodPoints: []
    });
    const [trackErrors, setTrackErrors] = useState({});

    useEffect(() => {
        const fetchDistance = async () => {
            setIsLoading(true);
            try {
                const eventRef = doc(db, 'events', eventId);
                const eventSnap = await getDoc(eventRef);

                if (eventSnap.exists()) {
                    const eventData = eventSnap.data();
                    if (distanceId !== 'new') {
                        const foundDistance = eventData.distances.find(d => d.id === distanceId);
                        if (foundDistance) {
                            setDistance({...initialDistanceState, ...foundDistance});

                            const urls = [];
                            if (foundDistance.trackImageUrl) urls.push(foundDistance.trackImageUrl);
                            if (foundDistance.trackUrl) urls.push(foundDistance.trackUrl);

                            setOldUrls(urls);
                        } else {
                            console.error('Дистанция не найдена');
                            navigate(`/events/${eventId}/distances`);
                        }
                    }
                }
            } catch (error) {
                console.error('Ошибка загрузки дистанции:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDistance();
    }, [eventId, distanceId]);

    const validateRequiredFields = () => {
        const newErrors = {
            id: !distance.id.trim(),
            title: !distance.title.trim(),
            distance: !distance.distance.trim(),
            startDate: !distance.startDate.trim(),
            foodPoints: []
        };

        if (Array.isArray(distance.foodPoints)) {
            distance.foodPoints.forEach((point, index) => {
                const itemErrors = {};
                if (!point.name || point.name.trim() === '') itemErrors.name = true;
                if (point.distance === null || point.distance === '' || isNaN(Number(point.distance))) itemErrors.distance = true;
                newErrors.foodPoints[index] = itemErrors;
            });
        }

        setErrors(newErrors);

        const hasFieldErrors = Object.values(newErrors).some(err =>
            Array.isArray(err)
                ? err.some(item => Object.values(item || {}).includes(true))
                : err
        );

        return !hasFieldErrors;
    };

    const cleanDistanceData = (data) => {
        const cleaned = {};

        for (const key in data) {
            const value = data[key];

            if (key === 'equipmentInfo') {
                if (
                    !value ||
                    (typeof value === 'object' &&
                        (!value.content || value.content.trim() === ''))
                ) {
                    cleaned[key] = null;
                } else {
                    cleaned[key] = cleanDistanceData(value);
                }
            } else if (typeof value === 'string') {
                cleaned[key] = value.trim() === '' ? null : value;
            } else if (typeof value === 'number') {
                cleaned[key] = value === 0 ? null : value;
            } else if (value && typeof value === 'object' && !Array.isArray(value)) {
                cleaned[key] = cleanDistanceData(value);
            } else {
                cleaned[key] = value;
            }
        }

        return cleaned;
    };

    const getTrackFileName = () => {
        if (trackFile) return trackFile.name;

        if (distance.trackUrl) {
            try {
                const decodedUrl = decodeURIComponent(distance.trackUrl);
                const match = decodedUrl.match(/\/([^\/]+\.gpx)/);
                if (match) return match[1];
            } catch (e) {
                console.error("Ошибка извлечения имени файла:", e);
            }
        }

        return null;
    };

    const handleTrackFileChange = (event) => {
        const file = event.target.files[0];
        const fileNameRegex = /^[a-zA-Z0-9._-]+$/;

        if (file) {
            if (!fileNameRegex.test(file.name)) {
                setTrackErrors({
                    track: "Имя файла должно содержать только латинские буквы, цифры, точки, дефисы или подчёркивания"
                });
            } else {
                setTrackFile(file);
                setTrackErrors((prevErrors) => {
                    const newErrors = { ...prevErrors };
                    delete newErrors.track;
                    return newErrors;
                });
            }
        }
    };

    const handleStatusChange = (e) => {
        setDistance({...distance, isEnabled: e.target.value === 'true'});
    };

    const onPreviewFileChange = (event) => {
        setPreviewImage(event.target.files[0]);
    };

    const uploadFile = async (file, folder) => {
        const storageRef = ref(storage, `${eventsCollectionName}/${eventId}/distances/${distance.title}/${folder}/` + file.name);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
    };

    const handleFoodPointChange = (index, key, value) => {
        const updatedPoints = [...distance.foodPoints];
        updatedPoints[index][key] = value;
        setDistance({...distance, foodPoints: updatedPoints});
    };

    const handleAddFoodPoint = () => {
        setDistance({
            ...distance,
            foodPoints: [
                ...distance.foodPoints,
                {
                    name: '',
                    distance: null,
                    closeTime: '',
                    food: '',
                    emergency: '',
                    evacuation: '',
                    support: null
                }
            ]
        });
    };

    const handleRemoveFoodPoint = (index) => {
        const updated = distance.foodPoints.filter((_, i) => i !== index);
        setDistance({...distance, foodPoints: updated});
    };

    const handleUpdateDistance = async () => {
        if (!validateRequiredFields()) return;

        setIsLoading(true);
        try {
            const cleanedDistance = cleanDistanceData({...distance});

            if (previewImage) {
                cleanedDistance.trackImageUrl = await uploadFile(previewImage, 'previewImage');
            }
            if (trackFile) {
                cleanedDistance.trackUrl = await uploadFile(trackFile, 'track');
            }

            const tempNewUrls = [];
            if (cleanedDistance.trackImageUrl) tempNewUrls.push(cleanedDistance.trackImageUrl);
            if (cleanedDistance.trackUrl) tempNewUrls.push(cleanedDistance.trackUrl);

            const urlsToDelete = oldUrls.filter(url => !tempNewUrls.includes(url));
            for (const url of urlsToDelete) {
                await deleteFileByUrl(url);
            }

            const eventRef = doc(db, 'events', eventId);
            const eventSnap = await getDoc(eventRef);

            if (eventSnap.exists()) {
                const eventData = eventSnap.data();
                let updatedDistances;

                if (distanceId === 'new') {
                    updatedDistances = [...(eventData.distances || []), cleanedDistance];
                } else {
                    updatedDistances = (eventData.distances || []).map(d => d.id === distanceId ? cleanedDistance : d);
                }

                await updateDoc(eventRef, {distances: updatedDistances});
                navigate(`/events/${eventId}/distances`);
            }
        } catch (error) {
            console.error('Ошибка сохранения дистанции:', error);
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className='right-content'>
            <div className='card shadow border-0'>
                <div className='form-group'>
                    <h4 className='hd'>Дистанция: </h4>
                </div>
                <div className='form-group'>
                    <div>
                        <h5>Статус:</h5>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <FormControl
                            className="w-auto"
                            sx={{
                                minWidth: 200,
                                marginRight: 5,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#CEFC4C',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#CEFC4C',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#CEFC4C',
                                    },
                                },
                            }}
                        >
                            <Select
                                value={distance.isEnabled.toString()}
                                onChange={handleStatusChange}
                                input={<OutlinedInput/>}
                                sx={{
                                    color: '#fff',
                                    '& .MuiSvgIcon-root': {
                                        color: '#fff',
                                    },
                                }}
                            >
                                <MenuItem value="false">Не доступно</MenuItem>
                                <MenuItem value="true">Доступно</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                </div>

                {/* Basic info */}
                <div style={{marginTop: 15}}>
                    <h5>Основная информация:</h5>
                </div>
                <div className='row form-group'>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            error={!!errors.id}
                            style={{marginTop: 15}}
                            value={distance.id || ''}
                            onChange={e => {
                                setDistance({...distance, id: e.target.value});
                                if (errors.id) {
                                    setErrors(prev => ({...prev, id: null}));
                                }
                            }}
                            InputProps={{
                                readOnly: distanceId !== 'new',
                            }}
                            label="ID дистанции"
                            sx={textFieldStyles}
                        />
                    </div>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            error={!!errors.title}
                            style={{marginTop: 15}}
                            value={distance.title || ''}
                            onChange={e => {
                                setDistance({...distance, title: e.target.value});
                                if (errors.title) {
                                    setErrors(prev => ({...prev, title: null}));
                                }
                            }}
                            label="Название"
                            sx={textFieldStyles}
                        />
                    </div>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            error={!!errors.distance}
                            style={{marginTop: 15}}
                            value={distance.distance || ''}
                            onChange={e => {
                                setDistance({...distance, distance: e.target.value});
                                if (errors.distance) {
                                    setErrors(prev => ({...prev, distance: null}));
                                }
                            }}
                            label="Дистанция"
                            sx={textFieldStyles}
                        />
                    </div>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            type="number"
                            style={{marginTop: 15}}
                            value={distance.elevationGain ?? ''}
                            onChange={e => {
                                const val = e.target.value;
                                setDistance({
                                    ...distance,
                                    elevationGain: val === '' ? null : Number(val),
                                });
                            }}
                            label="Набор высоты"
                            sx={textFieldStyles}
                        />
                    </div>
                </div>
                <div className='row form-group'>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            value={distance.description || ''}
                            onChange={e => setDistance({...distance, description: e.target.value})}
                            multiline
                            rows={4}
                            label="Описание"
                            sx={textFieldStyles}
                        />
                    </div>
                </div>

                {/* Date and time info */}
                <div style={{marginBottom: 15}}>
                    <h5>Информация о дате и времени:</h5>
                </div>
                <div className='row form-group'>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            error={!!errors.startDate}
                            value={distance.startDate || ''}
                            onChange={e => {
                                setDistance({...distance, startDate: e.target.value});
                                if (errors.startDate) {
                                    setErrors(prev => ({...prev, startDate: null}));
                                }
                            }}
                            label="Дата старта"
                            sx={textFieldStyles}
                        />
                    </div>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            value={distance.startTime || ''}
                            onChange={e => setDistance({...distance, startTime: e.target.value})}
                            label="Время старта"
                            sx={textFieldStyles}
                        />
                    </div>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            value={distance.timeLimit || ''}
                            onChange={e => setDistance({...distance, timeLimit: e.target.value})}
                            label="Лимит времени"
                            sx={textFieldStyles}
                        />
                    </div>
                </div>

                {/* Itra and Urls */}
                <div style={{marginBottom: 15}}>
                    <h5>Регистрация и ITRA:</h5>
                </div>
                <div className='row form-group'>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            value={distance.registrationUrl || ''}
                            onChange={e => setDistance({...distance, registrationUrl: e.target.value})}
                            label="Ссылка на регистрацию на дистанцию"
                            sx={textFieldStyles}
                        />
                    </div>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            type="number"
                            value={distance.itraPoints ?? ''}
                            onChange={e => {
                                const val = e.target.value;
                                setDistance({
                                    ...distance,
                                    itraPoints: val === '' ? null : Number(val),
                                });
                            }}
                            label="Кол-во баллов ITRA"
                            sx={textFieldStyles}
                        />
                    </div>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            value={distance.itraDetailsUrl || ''}
                            onChange={e => setDistance({...distance, itraDetailsUrl: e.target.value})}
                            label="Ссылка на ITRA"
                            sx={textFieldStyles}
                        />
                    </div>
                </div>

                {/* Photo service */}
                <div style={{marginBottom: 15}}>
                    <h5>Фото сервис:</h5>
                </div>
                <div className='row form-group'>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            value={distance.resultPhotoServiceApiId || ''}
                            onChange={e => setDistance({...distance, resultPhotoServiceApiId: e.target.value})}
                            label="Фото сервис Id"
                            sx={textFieldStyles}
                        />
                    </div>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            value={distance.resultPhotoServiceApiExternalId || ''}
                            onChange={e => setDistance({...distance, resultPhotoServiceApiExternalId: e.target.value})}
                            label="Фото сервис внешний Id"
                            sx={textFieldStyles}
                        />
                    </div>
                </div>

                {/* Track Image */}
                <div style={{marginTop: 10}}>
                    <h5>Превью изображение:</h5>
                </div>
                <div style={{width: 312}}>
                    <Card
                        sx={{width: 312, height: 214}}
                        className='mediaCard'
                    >
                        {previewImage ? (
                            <CardMedia
                                component="img"
                                sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                src={URL.createObjectURL(previewImage)}
                                alt="Preview Image"
                            />
                        ) : distance.trackImageUrl ? (
                            <CardMedia
                                component="img"
                                sx={{width: '100%', height: '100%', objectFit: 'crop'}}
                                src={distance.trackImageUrl}
                                alt="Preview Image"
                            />
                        ) : (
                            <CardContent
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                }}
                            >
                                <CiImageOn size={40}/>
                            </CardContent>
                        )}
                    </Card>
                    <Button
                        className='logInButton w-100'
                        variant="contained"
                        component="label"
                        sx={{marginTop: 2}}
                    >
                        Изменить
                        <input
                            type="file"
                            hidden
                            onChange={onPreviewFileChange}
                        />
                    </Button>
                </div>

                <div style={{marginTop: 20, width: 312}}>
                    <h5>GPX файл трека:</h5>
                    {getTrackFileName() && (
                        <div>
                            Имя файла: {getTrackFileName()}
                        </div>
                    )}
                    <Button
                        className='logInButton w-100'
                        variant="contained"
                        component="label"
                        error={!!trackErrors.track}
                    >
                        Загрузить GPX файл трека
                        <input
                            type="file"
                            accept=".gpx"
                            hidden
                            onChange={handleTrackFileChange}
                        />
                    </Button>
                    {trackErrors.track && (
                        <div style={{ color: 'red', marginTop: 8 }}>
                            {trackErrors.track}
                        </div>
                    )}
                    {(trackFile || distance.trackUrl) && (
                        <Button
                            variant="outlined"
                            color="error"
                            className="w-100"
                            style={{marginTop: 10}}
                            onClick={() => {
                                setDistance({...distance, trackUrl: null})
                                setTrackFile(null);
                            }}
                        >
                            Удалить файл трека
                        </Button>
                    )}
                    {trackFile && (
                        <div style={{marginTop: 10, color: '#CEFC4C'}}>
                            Загружен: {trackFile.name}
                        </div>
                    )}
                </div>

                {/* Equipments */}
                <div style={{marginTop: 15}}>
                    <h5>Необходимая экипировка HTML:</h5>
                </div>
                <div className='row form-group'>
                    <div className='col'>
                        <TextField
                            className='w-100'
                            value={distance.equipmentInfo?.content || ''}
                            onChange={e => setDistance({
                                ...distance,
                                equipmentInfo: {
                                    ...distance.equipmentInfo,
                                    content: e.target.value
                                }
                            })}
                            multiline
                            rows={5}
                            sx={textFieldStyles}
                        />
                    </div>
                </div>

                {/* Food Points */}
                <div style={{marginTop: 15}}>
                    <h5>Пункты питания (Food Points):</h5>
                </div>
                <div className='form-group'>
                    <ImageList cols={50} gap={20} style={{flexWrap: 'nowrap', overflowX: 'auto'}}>
                        {distance.foodPoints?.map((point, index) => (
                            <ImageListItem key={index} style={{width: 300}}>
                                <Card
                                    style={{
                                        backgroundColor: "transparent",
                                        borderColor: '#CEFC4C',
                                        borderStyle: "solid",
                                    }}
                                    sx={{width: '100%', height: 'auto'}}
                                >
                                    <CardContent>
                                        <TextField
                                            className='w-100'
                                            style={{marginTop: 10}}
                                            value={point.name || ''}
                                            onChange={(e) => {
                                                handleFoodPointChange(index, 'name', e.target.value);

                                                if (errors.foodPoints?.[index]?.name) {
                                                    setErrors(prev => {
                                                        const updatedErrors = [...(prev.foodPoints || [])];
                                                        if (!updatedErrors[index]) updatedErrors[index] = {};
                                                        updatedErrors[index].name = false;
                                                        return { ...prev, foodPoints: updatedErrors };
                                                    });
                                                }
                                            }}
                                            label="Название ПП"
                                            error={errors.foodPoints?.[index]?.name}
                                            required
                                            sx={textFieldStyles}
                                        />
                                        <TextField
                                            className='w-100'
                                            type="number"
                                            style={{marginTop: 10}}
                                            value={point.distance ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                handleFoodPointChange(index, 'distance', val === '' ? null : Number(val))

                                                if (errors.foodPoints?.[index]?.distance) {
                                                    setErrors(prev => {
                                                        const updatedErrors = [...(prev.foodPoints || [])];
                                                        if (!updatedErrors[index]) updatedErrors[index] = {};
                                                        updatedErrors[index].distance = false;
                                                        return { ...prev, foodPoints: updatedErrors };
                                                    });
                                                }
                                            }}
                                            label="Км расположения"
                                            error={errors.foodPoints?.[index]?.distance}
                                            required
                                            sx={textFieldStyles}
                                        />
                                        <TextField
                                            className='w-100'
                                            style={{marginTop: 10}}
                                            value={point.closeTime || ''}
                                            onChange={(e) => handleFoodPointChange(index, 'closeTime', e.target.value)}
                                            label="Время закрытия"
                                            sx={textFieldStyles}
                                        />
                                        <TextField
                                            className='w-100'
                                            style={{marginTop: 10}}
                                            value={point.food || ''}
                                            onChange={(e) => handleFoodPointChange(index, 'food', e.target.value)}
                                            label="Еда / напитки"
                                            multiline
                                            rows={2}
                                            sx={textFieldStyles}
                                        />
                                        <TextField
                                            className='w-100'
                                            style={{marginTop: 10}}
                                            value={point.emergency || ''}
                                            onChange={(e) => handleFoodPointChange(index, 'emergency', e.target.value)}
                                            label="Помощь (аптечка / скорая)"
                                            sx={textFieldStyles}
                                        />
                                        <TextField
                                            className='w-100'
                                            style={{marginTop: 10}}
                                            value={point.evacuation || ''}
                                            onChange={(e) => handleFoodPointChange(index, 'evacuation', e.target.value)}
                                            label="Эвакуация (да/нет)"
                                            sx={textFieldStyles}
                                        />
                                        <FormControl
                                            className="w-100"
                                            sx={{
                                                marginTop: '10px',
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: '#CEFC4C',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#CEFC4C',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#CEFC4C',
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: '#fff',
                                                    '&.Mui-focused': {
                                                        color: '#fff',
                                                    },
                                                },
                                            }}
                                        >
                                            <InputLabel>Поддержка</InputLabel>
                                            <Select
                                                value={point.support || ''}
                                                onChange={(e) => handleFoodPointChange(index, 'support', e.target.value)}
                                                label="Поддержка"
                                                input={<OutlinedInput label="Поддержка"/>}
                                                sx={{
                                                    color: '#fff',
                                                    '& .MuiSvgIcon-root': {
                                                        color: '#fff',
                                                    },
                                                }}
                                            >
                                                <MenuItem value="Да">Да</MenuItem>
                                                <MenuItem value="Нет">Нет</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Button
                                            className='logInButton w-100'
                                            variant="contained"
                                            sx={{marginTop: 10}}
                                            onClick={() => handleRemoveFoodPoint(index)}
                                        >
                                            Удалить ПП
                                        </Button>
                                    </CardContent>
                                </Card>
                            </ImageListItem>
                        ))}
                        <ImageListItem style={{width: 300, height: 'auto'}}>
                            <Card
                                sx={{width: '100%', height: '100%', cursor: 'pointer'}}
                                className='mediaCard'
                                onClick={handleAddFoodPoint}
                            >
                                <CardContent
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                    }}
                                >
                                    <CiSquarePlus size={80}/>
                                </CardContent>
                            </Card>
                        </ImageListItem>
                    </ImageList>
                </div>

                <Button
                    className='logInButton w-100'
                    variant='contained'
                    onClick={handleUpdateDistance}
                >
                    Сохранить
                </Button>
            </div>
        </div>
    );
};

const textFieldStyles = {
    '& .MuiSvgIcon-root': {
        color: '#fff',
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#CEFC4C',
        },
        '&:hover fieldset': {
            borderColor: '#CEFC4C',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#CEFC4C',
        },
        '& input': {
            color: '#fff',
        },
        '& textarea': {
            color: '#fff',
        },
    },
    '& .MuiInputLabel-root': {
        color: '#fff',
        '&.Mui-focused': {
            color: '#fff',
        },
    },
};

export default DistanceEditPage;